import { CheckCircleIcon, ListPlusIcon, MegaphoneIcon, MinusCircleIcon, SparkleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import type { Insight } from '@regia/shared';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { errorMessage, http } from '../../lib/api';
import { formatAgo, formatDayShort } from '../../lib/format';
import { Button, ButtonLink, cn, EmptyState, ListGroup, Section, Skeleton } from '../../ui';
import type { SchedaTabProps } from '../registry';
import { aiReady, useActionToTask, useAiConfig, useAnalyze, useDraftReplies, useDraftWeek, useInsights } from './api';

const TONE = {
  good: { icon: CheckCircleIcon, cls: 'text-ok' },
  bad: { icon: WarningCircleIcon, cls: 'text-alert' },
  neutral: { icon: MinusCircleIcon, cls: 'text-ink-3' },
} as const;

/** Avviso quando l'AI non è pronta, con il motivo e dove sistemarlo. */
export function AiNotReady() {
  const config = useAiConfig();
  const c = config.data;
  if (!c || aiReady(c)) return null;
  const cli = c.provider === 'claude-cli';
  const reason = !c.enabled
    ? 'L’AI è spenta.'
    : cli
      ? 'Manca un passo: collegare il tuo account Claude.'
      : c.monthCostEur >= c.monthlyBudgetEur
        ? `Tetto di spesa del mese raggiunto (${c.monthlyBudgetEur} €).`
        : 'Per usare l’AI serve una chiave di Claude (Anthropic).';
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-[var(--radius-sheet)] bg-sheet p-5">
      <div className="flex items-start gap-3">
        <SparkleIcon size={24} className="shrink-0 text-action" aria-hidden />
        <p className="text-[0.9375rem] text-ink-2">
          {reason}{' '}
          {cli
            ? 'Si fa una volta sola dalle Impostazioni, alla voce AI: entri con il tuo account e Claude resta acceso sul server.'
            : 'Si sistema nelle Impostazioni, alla voce AI: spendi solo quando la usi, con un tetto mensile.'}
        </p>
      </div>
      <ButtonLink to="/impostazioni#ai" variant="secondary" size="sm" className="self-start">
        {cli ? 'Collega Claude' : 'Vai alle impostazioni dell’AI'}
      </ButtonLink>
    </div>
  );
}

function InsightView({ insight, pageId }: { insight: Insight; pageId: string }) {
  const toTask = useActionToTask(pageId);
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-[var(--radius-sheet)] bg-sheet p-5">
        <p className="text-sm text-ink-3">Analisi del {formatDayShort(insight.createdAt)}</p>
        <p className="mt-2 max-w-prose text-lg leading-relaxed text-ink">{insight.summary}</p>
      </div>
      {insight.findings.length ? (
        <ListGroup>
          {insight.findings.map((f) => {
            const tone = TONE[f.tone] ?? TONE.neutral;
            return (
              <li key={f.title} className="flex items-start gap-3 px-4 py-3">
                <tone.icon size={22} weight="fill" className={cn('mt-0.5 shrink-0', tone.cls)} aria-hidden />
                <div className="min-w-0">
                  <p className="text-[0.9375rem] font-semibold text-ink">{f.title}</p>
                  <p className="text-[0.9375rem] text-ink-2">{f.detail}</p>
                </div>
              </li>
            );
          })}
        </ListGroup>
      ) : null}
      {insight.actions.length ? (
        <div>
          <h4 className="mb-2 px-1 text-sm font-semibold text-ink-2">Cosa fare questa settimana</h4>
          <ListGroup>
            {insight.actions.map((a, i) => (
              <li key={a.title} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <p className="text-[0.9375rem] font-semibold text-ink">{a.title}</p>
                  <p className="text-sm text-ink-2">{a.why}</p>
                </div>
                {a.taskId ? (
                  <Link to={`/attivita?apri=${a.taskId}`} className="shrink-0 text-sm font-semibold text-ok hover:underline">
                    Attività creata
                  </Link>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={ListPlusIcon}
                    className="shrink-0 self-start"
                    loading={toTask.isPending && toTask.variables?.index === i}
                    onClick={() =>
                      toTask.mutate({ id: insight.id, index: i }, { onSuccess: () => toast.success('Aggiunta alle attività'), onError: (e) => toast.error(errorMessage(e)) })
                    }
                  >
                    Crea attività
                  </Button>
                )}
              </li>
            ))}
          </ListGroup>
        </div>
      ) : null}
    </div>
  );
}

export function SchedaInsights({ page }: SchedaTabProps) {
  const config = useAiConfig();
  const ready = aiReady(config.data);
  const insights = useInsights(page.id);
  const analyze = useAnalyze(page.id);
  const drafts = useDraftWeek(page.id);
  const replies = useDraftReplies(page.id);
  const latest = insights.data?.[0];

  useEffect(() => {
    if (latest && !latest.seenAt) void http.post(`/api/insights/${latest.id}/seen`).catch(() => undefined);
  }, [latest]);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-x-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
      <div>
        <AiNotReady />
        {insights.isLoading ? (
          <Skeleton className="h-48 w-full rounded-[var(--radius-sheet)]" />
        ) : latest ? (
          <InsightView insight={latest} pageId={page.id} />
        ) : (
          <EmptyState
            icon={SparkleIcon}
            title="Nessuna analisi ancora"
            message={`Ogni lunedì l’AI guarda i numeri di ${page.name} e ti dice cosa va, cosa no e cosa fare. Puoi anche chiederla adesso.`}
          />
        )}
        {insights.data && insights.data.length > 1 ? (
          <Section title="Analisi precedenti" className="mt-8">
            <ListGroup>
              {insights.data.slice(1).map((i) => (
                <li key={i.id} className="px-4 py-3">
                  <p className="text-sm text-ink-3">{formatDayShort(i.createdAt)}</p>
                  <p className="text-[0.9375rem] text-ink-2">{i.summary}</p>
                </li>
              ))}
            </ListGroup>
          </Section>
        ) : null}
      </div>
      <div className="mt-8 lg:mt-0">
        <Section title="Chiedi all’AI">
          <div className="flex flex-col gap-3 rounded-[var(--radius-sheet)] bg-sheet p-4">
            <Button
              icon={SparkleIcon}
              disabled={!ready}
              loading={analyze.isPending}
              onClick={() => analyze.mutate(undefined, { onSuccess: () => toast.success('Analisi pronta'), onError: (e) => toast.error(errorMessage(e)) })}
            >
              Analizza adesso
            </Button>
            <Button
              variant="secondary"
              icon={MegaphoneIcon}
              disabled={!ready}
              loading={drafts.isPending}
              onClick={() =>
                drafts.mutate(undefined, {
                  onSuccess: (r) => toast.success(r.created ? `${r.created} bozze nel Piano` : 'Il piano della settimana è già pieno'),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              Prepara le bozze della settimana
            </Button>
            <Button
              variant="secondary"
              disabled={!ready}
              loading={replies.isPending}
              onClick={() =>
                replies.mutate(undefined, {
                  onSuccess: (r) => toast.success(r.drafted ? `${r.drafted} risposte pronte nei Numeri` : 'Nessuna recensione nuova da rispondere'),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              Risposte alle recensioni
            </Button>
            <p className="text-sm text-ink-3">
              {config.data
                ? config.data.provider === 'claude-cli'
                  ? 'Usa il tuo abbonamento Claude: nessun costo in più.'
                  : `Spesi circa ${config.data.monthCostEur.toFixed(2)} € su ${config.data.monthlyBudgetEur} € questo mese.`
                : ''}
              {latest ? ` Ultima analisi ${formatAgo(latest.createdAt)}.` : ''}
            </p>
          </div>
        </Section>
      </div>
    </div>
  );
}
